import { useEffect } from 'react';

import type { CmsPage, FaqBlock, SiteSettings } from '@saas-bot/types';

import { BlockRenderer } from './blocks';

function upsertMeta(
  selector: string,
  attr: 'name' | 'property',
  key: string,
  content: string,
): void {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setMeta(name: string, content: string): void {
  upsertMeta(`meta[name="${name}"]`, 'name', name, content);
}

function setOg(property: string, content: string): void {
  upsertMeta(`meta[property="${property}"]`, 'property', property, content);
}

function setCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

/** Structured data (JSON-LD): Organization + WebSite globally, WebPage per page,
 *  and FAQPage when the page has an FAQ block. Rewritten on every navigation. */
function setStructuredData(page: CmsPage, settings: SiteSettings): void {
  const origin = window.location.origin;
  const graph: unknown[] = [
    {
      '@type': 'Organization',
      name: settings.brandName,
      url: origin,
      logo: `${origin}${settings.logoUrl}`,
    },
    {
      '@type': 'WebSite',
      name: settings.brandName,
      url: origin,
    },
    {
      '@type': 'WebPage',
      name: page.seoTitle ?? page.title,
      description: page.seoDescription ?? settings.seo.defaultDescription,
      url: window.location.href,
    },
  ];

  const faq = page.blocks.find((block): block is FaqBlock & { id: string } => block.type === 'faq');
  if (faq && faq.items.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: faq.items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    });
  }

  let script = document.head.querySelector<HTMLScriptElement>('#vx-jsonld');
  if (!script) {
    script = document.createElement('script');
    script.id = 'vx-jsonld';
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}

export function PageView({
  page,
  settings,
}: {
  page: CmsPage;
  settings: SiteSettings;
}): JSX.Element {
  useEffect(() => {
    const title = page.seoTitle ?? `${page.title} · ${settings.brandName}`;
    const description = page.seoDescription ?? settings.seo.defaultDescription;
    const ogImage = page.seoImageUrl ? `${window.location.origin}${page.seoImageUrl}` : null;

    document.title = title;
    setMeta('description', description);
    setMeta('robots', page.noindex ? 'noindex, nofollow' : 'index, follow');
    setCanonical(page.canonicalUrl || window.location.href);

    setOg('og:type', 'website');
    setOg('og:site_name', settings.brandName);
    setOg('og:title', page.ogTitle ?? title);
    setOg('og:description', page.ogDescription ?? description);
    setOg('og:url', window.location.href);
    if (ogImage) {
      setOg('og:image', ogImage);
      setMeta('twitter:card', 'summary_large_image');
    }

    setStructuredData(page, settings);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [page, settings]);

  return (
    <>
      {page.blocks.map((block) => (
        <BlockRenderer key={block.id} block={block} />
      ))}
    </>
  );
}
